import { useEffect, useMemo, useRef, useState } from "react";
import { trpc } from "../../../lib/trpc";
import { Icon } from "../../ui/Icon";
import { Drawer } from "../../Drawer";
import { SearchSelect } from "../../ui/SearchSelect";
import { ToggleSwitch } from "../../ui/ToggleSwitch";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { ToastStack, type ToastItem } from "../../ui/Toast";
import { OvertimeRateDrawer, fmtRate } from "../OvertimeRateDrawer";
import { PositionPayCard } from "../PositionPayCard";
import { useIsMobile } from "../../../lib/useIsMobile";
import { MCard } from "../../../components/mobile/primitives";
import "../../../pages/Items.css";

/**
 * İnsan Kaynakları · Pozisyonlar sekmesi.
 *
 * Kadro/rol düğümlerinin düz listesi + oluştur/düzenle/sil. Düzenleme drawer'ı
 * pozisyon künyesini VE gömülü görev tanımını (özet/sorumluluklar/nitelikler) yönetir:
 * kaydederken önce `position.create|update`, ardından (içerik varsa ya da tanım zaten
 * varsa) `jobDescription.upsert` çağrılır — tek "Kaydet" ile iki uç güncellenir.
 *
 * Yazma aksiyonları yalnız `canManage` (manage:hr). `isSuper` çok-tesisli görünüm için
 * "Tesis" kolonunu açar (tesisName null = Global).
 */

type EmploymentType = "full_time" | "part_time" | "contractor" | "intern" | "temporary";

const EMPLOYMENT_LABELS: Record<EmploymentType, string> = {
  full_time: "Tam zamanlı",
  part_time: "Yarı zamanlı",
  contractor: "Sözleşmeli / Taşeron",
  intern: "Stajyer",
  temporary: "Geçici",
};

/** Durum = renk + ikon + metin (CLAUDE.md #5). */
const STATUS_META: Record<"active" | "passive", { cls: string; label: string; icon: string }> = {
  active: { cls: "b-success", label: "Aktif", icon: "check" },
  passive: { cls: "", label: "Pasif", icon: "minus" },
};

interface PositionListRow {
  id: string;
  code: string;
  title: string;
  headcount: number;
  isManager: boolean;
  isShiftBased: boolean;
  isDeclarable: boolean;
  productionCenterId: string | null;
  productionCenterName: string | null;
  employmentType: string;
  isActive: boolean;
  orgUnitId: string | null;
  orgUnitName: string | null;
  reportsToId: string | null;
  reportsToTitle: string | null;
  tesisId: string | null;
  tesisName: string | null;
  hasJobDescription: boolean;
  employeeCount: number;
}

export function PositionsTab({ canManage, isSuper }: { canManage: boolean; isSuper: boolean }) {
  const qc = trpc.useUtils();
  const isMobile = useIsMobile();

  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const toastSeq = useRef(0);
  const pushToast = (t: Omit<ToastItem, "id">) =>
    setToasts((p) => [...p.slice(-4), { ...t, id: String(++toastSeq.current) }]);

  const [form, setForm] = useState<{ mode: "new" } | { mode: "edit"; id: string } | null>(null);
  const [delTarget, setDelTarget] = useState<PositionListRow | null>(null);
  const [rateFor, setRateFor] = useState<PositionListRow | null>(null);

  const listQ = trpc.hr.position.list.useQuery(undefined, { staleTime: 5_000 });
  const rows = (listQ.data ?? []) as PositionListRow[];

  // Mesai saat ücreti PARA'dır → yalnız süper-admin. `position.list` bu alanı bilinçli
  // DÖNDÜRMEZ (manage:hr olan İK kullanıcısına sızmasın); ayrı uçtan tek sorguda çekilir.
  const rateQ = trpc.hr.overtimeRate.currentAll.useQuery(undefined, { enabled: isSuper, staleTime: 5_000 });
  const rateByPos = useMemo(() => {
    const m = new Map<string, { amount: number; currency: string; upcoming: boolean; effectiveFrom: Date | string }>();
    for (const r of rateQ.data ?? []) {
      m.set(r.positionId, { amount: r.amount, currency: r.currency, upcoming: r.upcoming, effectiveFrom: r.effectiveFrom });
    }
    return m;
  }, [rateQ.data]);
  /** Oran hücresi: bugün yürürlükte yoksa "planlanan" olarak, başlangıç tarihiyle gösterilir. */
  const rateCell = (id: string) => {
    const r = rateByPos.get(id);
    if (!r) return <span style={{ color: "var(--neutral-400)" }}>—</span>;
    const from = new Date(r.effectiveFrom).toLocaleDateString("tr-TR");
    return (
      <>
        {fmtRate(r.amount)} <span className="unit">{r.currency}</span>
        {r.upcoming ? <span className="pay-sub" title={`Bu oran ${from} tarihinde yürürlüğe girecek`}>{from}'den</span> : null}
      </>
    );
  };

  /**
   * Pozisyonun TEMEL MAAŞI — mesai oranıyla AYNI rejim: PARA olduğu için `position.list`
   * bu alanı döndürmez, ayrı süper-admin ucundan tek sorguda çekilir.
   */
  const salQ = trpc.hr.positionSalary.currentAll.useQuery(undefined, { enabled: isSuper, staleTime: 5_000 });
  const salByPos = useMemo(() => {
    const m = new Map<string, { amount: number; currency: string; upcoming: boolean; effectiveFrom: Date | string }>();
    for (const r of salQ.data ?? []) {
      m.set(r.positionId, { amount: r.amount, currency: r.currency, upcoming: r.upcoming, effectiveFrom: r.effectiveFrom });
    }
    return m;
  }, [salQ.data]);
  /** Maaş hücresi — `rateCell` ile aynı kalıp; ileri tarihli değer "planlanan" olarak çıkar. */
  const salaryCell = (id: string) => {
    const r = salByPos.get(id);
    if (!r) return <span style={{ color: "var(--neutral-400)" }}>—</span>;
    const from = new Date(r.effectiveFrom).toLocaleDateString("tr-TR");
    return (
      <>
        {new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(r.amount)}{" "}
        <span className="unit">{r.currency}</span>
        {r.upcoming ? <span className="pay-sub" title={`Bu maaş ${from} tarihinde yürürlüğe girecek`}>{from}'den</span> : null}
      </>
    );
  };

  const removeMut = trpc.hr.position.remove.useMutation({
    onSuccess: () => {
      qc.hr.position.invalidate();
      setDelTarget(null);
      pushToast({ tone: "success", message: "Pozisyon silindi." });
    },
    onError: (e) => pushToast({ tone: "danger", title: "Silinemedi", message: e.message }),
  });

  const positionRefs = useMemo(() => rows.map((r) => ({ id: r.id, title: r.title })), [rows]);

  const drawers = (
    <>
      <PositionFormDrawer
        key={form ? (form.mode === "edit" ? `edit-${form.id}` : "new") : "closed"}
        open={!!form}
        positionId={form?.mode === "edit" ? form.id : null}
        positions={positionRefs}
        isSuper={isSuper}
        onClose={() => setForm(null)}
        onSaved={() => qc.hr.position.invalidate()}
        pushToast={pushToast}
      />

      <OvertimeRateDrawer
        key={rateFor ? `rate-${rateFor.id}` : "rate-closed"}
        open={!!rateFor}
        position={rateFor}
        onClose={() => setRateFor(null)}
        pushToast={pushToast}
      />

      <ConfirmDialog
        open={!!delTarget}
        title="Pozisyonu sil"
        message={
          delTarget ? (
            <>
              <p style={{ margin: 0 }}>
                <b>{delTarget.title}</b> (<span style={{ fontFamily: "var(--font-mono)" }}>{delTarget.code}</span>)
                {" "}pozisyonu silinecek. Bu işlem geri alınamaz.
              </p>
              {delTarget.employeeCount > 0 ? (
                <p style={{ margin: "8px 0 0", color: "var(--danger)" }}>
                  Bu pozisyona bağlı {new Intl.NumberFormat("tr-TR").format(delTarget.employeeCount)} personel var —
                  önce onları başka pozisyona taşımalısınız.
                </p>
              ) : null}
            </>
          ) : ""
        }
        confirmLabel="Sil"
        tone="danger"
        busy={removeMut.isPending}
        onCancel={() => setDelTarget(null)}
        onConfirm={() => delTarget && removeMut.mutate({ id: delTarget.id })}
      />

      <ToastStack toasts={toasts} onDismiss={(id) => setToasts((p) => p.filter((t) => t.id !== id))} />
    </>
  );

  if (isMobile) {
    const mb = (c: string) => (c === "b-warn" ? "b-warning" : c || "b-neutral");
    return (
      <>
        <div className="m-stickytop">
          {canManage ? (
            <button
              type="button"
              className="btn-md primary sm"
              style={{ justifyContent: "center" }}
              onClick={() => setForm({ mode: "new" })}
            >
              <Icon name="plus" size={14} />Pozisyon Ekle
            </button>
          ) : null}
        </div>

        {listQ.isError ? (
          <div className="m-empty">{listQ.error.message}</div>
        ) : listQ.isLoading ? (
          <div className="m-empty">Yükleniyor…</div>
        ) : rows.length === 0 ? (
          <div className="m-empty">
            {canManage
              ? '"Pozisyon Ekle" ile ilk kadro/rolü tanımlayın.'
              : "Görüntülenecek pozisyon bulunmuyor."}
          </div>
        ) : (
          rows.map((p) => {
            const st = STATUS_META[p.isActive ? "active" : "passive"];
            return (
              <MCard pad key={p.id}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", fontSize: 13.5, fontWeight: 700, color: "var(--neutral-900)" }}>
                      {p.title}
                      {p.hasJobDescription ? (
                        <span title="Görev tanımı tanımlı" style={{ color: "var(--neutral-400)", display: "inline-flex", flexShrink: 0 }}>
                          <Icon name="fileText" size={12} />
                        </span>
                      ) : null}
                      {p.isManager ? (
                        <span className="badge b-primary no-dot" style={{ flexShrink: 0 }}><Icon name="star" size={10} />Yönetici</span>
                      ) : null}
                      {p.isShiftBased ? <span className="badge b-info no-dot" style={{ flexShrink: 0 }}><Icon name="clock" size={10} />Vardiyalı</span> : null}
                      {p.isDeclarable ? <span className="badge b-warning no-dot" style={{ flexShrink: 0 }}><Icon name="users" size={10} />Beyan</span> : null}
                      {p.productionCenterName ? <span className="badge b-neutral no-dot" style={{ flexShrink: 0 }}><Icon name="factory" size={10} />{p.productionCenterName}</span> : null}
                    </span>
                    <span className="mono" style={{ display: "block", fontSize: 11, color: "var(--neutral-400)" }}>{p.code}</span>
                  </span>
                  <span className={`badge ${mb(st.cls)} no-dot`}><Icon name={st.icon} size={11} />{st.label}</span>
                </div>
                <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div className="m-ovl">Birim</div>
                    <div style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{p.orgUnitName ?? "—"}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="m-ovl">Kadro</div>
                    <div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{new Intl.NumberFormat("tr-TR").format(p.headcount)}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="m-ovl">Personel</div>
                    <div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{new Intl.NumberFormat("tr-TR").format(p.employeeCount)}</div>
                  </div>
                </div>
                {(p.reportsToTitle || isSuper) ? (
                  <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 11.5, color: "var(--neutral-600)", flexWrap: "wrap" }}>
                    {p.reportsToTitle ? <span><span style={{ color: "var(--neutral-400)" }}>Üst </span>{p.reportsToTitle}</span> : null}
                    {isSuper ? <span><span style={{ color: "var(--neutral-400)" }}>Tesis </span>{p.tesisName ?? "Global"}</span> : null}
                  </div>
                ) : null}
                {isSuper ? (
                  <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 11.5, color: "var(--neutral-600)" }}>
                    <span>
                      <span style={{ color: "var(--neutral-400)" }}>Mesai saat ücreti </span>
                      <span className="mono">{rateCell(p.id)}</span>
                    </span>
                  </div>
                ) : null}
                {canManage || isSuper ? (
                  <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }}>
                    {isSuper ? <button type="button" className="m-iconbtn m-li-act" aria-label={`${p.title}: fazla mesai saat ücreti`} onClick={() => setRateFor(p)}><Icon name="clock" size={16} /></button> : null}
                    {canManage ? <button type="button" className="m-iconbtn m-li-act" aria-label={`${p.title}: düzenle`} onClick={() => setForm({ mode: "edit", id: p.id })}><Icon name="edit" size={16} /></button> : null}
                    {canManage ? <button type="button" className="m-iconbtn m-li-act danger" aria-label={`${p.title}: sil`} onClick={() => setDelTarget(p)}><Icon name="trash" size={16} /></button> : null}
                  </div>
                ) : null}
              </MCard>
            );
          })
        )}

        {drawers}
      </>
    );
  }

  return (
    <div>
      <div className="items-head">
        <p className="sub">Kadro ve roller — bağlı birim, üst pozisyon, kadro sayısı ve görev tanımı.</p>
        {canManage ? (
          <button type="button" className="btn-md primary" onClick={() => setForm({ mode: "new" })}>
            <Icon name="plus" size={14} />Pozisyon Ekle
          </button>
        ) : null}
      </div>

      <div className="dt2-wrap">
        <div className="dt2-scroll">
          {listQ.isError ? (
            <div className="empty-state">
              <p className="ttl">Yüklenemedi</p>
              <p className="sub">{listQ.error.message}</p>
            </div>
          ) : listQ.isLoading ? (
            <div className="empty-state">
              <p className="ttl">Yükleniyor…</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="empty-state">
              <span className="ic" aria-hidden="true"><Icon name="users" size={30} /></span>
              <p className="ttl">Henüz pozisyon yok</p>
              <p className="sub">
                {canManage
                  ? '"Pozisyon Ekle" ile ilk kadro/rolü tanımlayın.'
                  : "Görüntülenecek pozisyon bulunmuyor."}
              </p>
            </div>
          ) : (
            <table className="dt2">
              <thead>
                <tr>
                  <th style={{ width: 150 }}>Kod</th>
                  <th className="pos-col">Başlık</th>
                  <th>Birim</th>
                  <th>Üst Pozisyon</th>
                  <th className="num" style={{ width: 80 }}>Kadro</th>
                  <th style={{ width: 120 }}>Yönetici</th>
                  <th className="num" style={{ width: 90 }}>Personel</th>
                  {isSuper ? <th className="num" style={{ width: 140 }} title="Bu pozisyondaki personelin aylık brütü — kişiye özel maaşı olanlar hariç">Pozisyon Maaşı</th> : null}
                  {isSuper ? <th className="num" style={{ width: 130 }} title="Bir saatlik fazla mesainin ücreti">Mesai Saat Ücr.</th> : null}
                  {isSuper ? <th style={{ width: 140 }}>Tesis</th> : null}
                  <th style={{ width: 110 }}>Durum</th>
                  {canManage || isSuper ? <th style={{ width: 120 }} aria-label="İşlemler" /> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const st = STATUS_META[p.isActive ? "active" : "passive"];
                  return (
                    <tr key={p.id}>
                      <td className="cell-id">{p.code}</td>
                      <td className="pos-col">
                        {/* İKİ SATIR: ad üstte, etiketler altta. Tek satırda dururken
                            "Vardiyalı + Beyan + hat adı" başlık sütununu tablonun yarısına
                            kadar şişiriyordu. */}
                        <span className="pos-title">
                          {p.title}
                          {p.hasJobDescription ? (
                            <span className="pos-jd" title="Görev tanımı tanımlı">
                              <Icon name="fileText" size={11} />
                            </span>
                          ) : null}
                        </span>
                        {p.isShiftBased || p.isDeclarable || p.productionCenterName ? (
                          <span className="pos-tags">
                            {p.isShiftBased ? <span className="badge-pill b-info" title="Vardiyalı çalışır"><Icon name="clock" size={9} />Vardiyalı</span> : null}
                            {p.isDeclarable ? <span className="badge-pill b-warn" title="Vardiya devam beyanına tabi"><Icon name="users" size={9} />Beyan</span> : null}
                            {p.productionCenterName ? <span className="badge-pill" title="Atanmış üretim merkezi — beyan yalnız bu merkezde"><Icon name="factory" size={9} />{p.productionCenterName}</span> : null}
                          </span>
                        ) : null}
                      </td>
                      <td>{p.orgUnitName ?? <span style={{ color: "var(--neutral-400)" }}>—</span>}</td>
                      <td>{p.reportsToTitle ?? <span style={{ color: "var(--neutral-400)" }}>—</span>}</td>
                      <td className="cell-num">{new Intl.NumberFormat("tr-TR").format(p.headcount)}</td>
                      <td>
                        {p.isManager ? (
                          <span className="badge-pill b-primary"><Icon name="star" size={11} />Yönetici</span>
                        ) : (
                          <span style={{ color: "var(--neutral-400)" }}>—</span>
                        )}
                      </td>
                      <td className="cell-num">{new Intl.NumberFormat("tr-TR").format(p.employeeCount)}</td>
                      {isSuper ? (
                        <td className="cell-num">{salaryCell(p.id)}</td>
                      ) : null}
                      {isSuper ? (
                        <td className="cell-num">{rateCell(p.id)}</td>
                      ) : null}
                      {isSuper ? (
                        <td>
                          {p.tesisName ?? <span style={{ color: "var(--neutral-400)" }}>Global</span>}
                        </td>
                      ) : null}
                      <td>
                        <span className={`badge-pill ${st.cls}`}><Icon name={st.icon} size={11} />{st.label}</span>
                      </td>
                      {canManage || isSuper ? (
                        <td>
                          <span style={{ display: "inline-flex", gap: 4 }}>
                            {isSuper ? (
                              <button
                                type="button"
                                className="iact"
                                title="Fazla mesai saat ücreti"
                                aria-label={`${p.title}: fazla mesai saat ücreti`}
                                onClick={() => setRateFor(p)}
                              >
                                <Icon name="clock" size={14} />
                              </button>
                            ) : null}
                            {canManage ? (
                              <>
                                <button
                                  type="button"
                                  className="iact"
                                  title="Düzenle"
                                  aria-label={`${p.title}: düzenle`}
                                  onClick={() => setForm({ mode: "edit", id: p.id })}
                                >
                                  <Icon name="edit" size={14} />
                                </button>
                                <button
                                  type="button"
                                  className="iact danger"
                                  title="Sil"
                                  aria-label={`${p.title}: sil`}
                                  onClick={() => setDelTarget(p)}
                                >
                                  <Icon name="trash" size={14} />
                                </button>
                              </>
                            ) : null}
                          </span>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="dt2-foot">
          <span>{listQ.data ? `${rows.length} pozisyon` : "…"}</span>
        </div>
      </div>

      {drawers}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Serbest metin listesi editörü (sorumluluklar / nitelikler).
   Modül seviyesinde tanımlıdır — her render'da yeniden mount edilip input
   odağını kaybetmesin diye (nested component tuzağı).
   ══════════════════════════════════════════════════════════════════════════ */
function StringListEditor({
  items,
  onChange,
  addLabel,
  placeholder,
}: {
  items: string[];
  onChange: (next: string[]) => void;
  addLabel: string;
  placeholder: string;
}) {
  const update = (i: number, v: string) => onChange(items.map((x, idx) => (idx === i ? v : x)));
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, ""]);
  return (
    <div>
      {items.length === 0 ? (
        <p className="help" style={{ marginBottom: 8 }}>Henüz madde yok.</p>
      ) : (
        items.map((it, i) => (
          <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
            <input
              className="form-input"
              value={it}
              placeholder={placeholder}
              maxLength={500}
              onChange={(e) => update(i, e.target.value)}
            />
            <button
              type="button"
              className="input-icon-btn"
              aria-label={`${i + 1}. maddeyi kaldır`}
              onClick={() => remove(i)}
            >
              <Icon name="x" size={14} />
            </button>
          </div>
        ))
      )}
      <button type="button" className="btn-md ghost" onClick={add}>
        <Icon name="plus" size={14} />{addLabel}
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Pozisyon oluştur / düzenle drawer'ı (görev tanımı gömülü).
   Parent her açılışta `key` ile YENİDEN mount eder → taze state, bayat prefill yok.
   ══════════════════════════════════════════════════════════════════════════ */
function PositionFormDrawer({
  open,
  positionId,
  positions,
  isSuper,
  onClose,
  onSaved,
  pushToast,
}: {
  open: boolean;
  positionId: string | null;
  positions: { id: string; title: string }[];
  /** Ücretlendirme bölümü YALNIZ süper-adminde çizilir (uçlar da öyle korunur). */
  isSuper: boolean;
  onClose: () => void;
  onSaved: () => void;
  pushToast: (t: Omit<ToastItem, "id">) => void;
}) {
  const isEdit = !!positionId;
  const qc = trpc.useUtils();

  const [title, setTitle] = useState("");
  const [orgUnitId, setOrgUnitId] = useState<string | null>(null);
  const [reportsToId, setReportsToId] = useState<string | null>(null);
  const [headcount, setHeadcount] = useState("1");
  const [isManager, setIsManager] = useState(false);
  const [isShiftBased, setIsShiftBased] = useState(false);
  const [isDeclarable, setIsDeclarable] = useState(false);
  const [productionCenterId, setProductionCenterId] = useState<string | null>(null);
  const [employmentType, setEmploymentType] = useState<EmploymentType>("full_time");
  const [isActive, setIsActive] = useState(true);
  const [summary, setSummary] = useState("");
  const [responsibilities, setResponsibilities] = useState<string[]>([]);
  const [requirements, setRequirements] = useState<string[]>([]);
  const [jdExisted, setJdExisted] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const orgUnitsQ = trpc.hr.orgUnit.list.useQuery(undefined, { enabled: open, staleTime: 60_000 });
  const getQ = trpc.hr.position.get.useQuery(
    { id: positionId ?? "" },
    { enabled: open && isEdit, staleTime: 3_000 },
  );

  // Düzenlemede alanlar `getQ.data` (mevcut pozisyon) gelince dolar. Görev tanımı
  // position.get içine gömülü döndüğü için ayrı sorgu gerekmez.
  useEffect(() => {
    const p = getQ.data;
    if (!p) return;
    setTitle(p.title);
    setOrgUnitId(p.orgUnitId ?? null);
    setReportsToId(p.reportsToId ?? null);
    setHeadcount(String(p.headcount));
    setIsManager(p.isManager);
    setIsShiftBased(p.isShiftBased);
    setIsDeclarable(p.isDeclarable);
    setProductionCenterId(p.productionCenterId ?? null);
    setEmploymentType(p.employmentType as EmploymentType);
    setIsActive(p.isActive);
    const jd = p.jobDescription;
    setJdExisted(!!jd);
    setSummary(jd?.summary ?? "");
    setResponsibilities(jd?.responsibilities ?? []);
    setRequirements(jd?.requirements ?? []);
  }, [getQ.data]);

  const orgUnitOptions = useMemo(
    () => (orgUnitsQ.data ?? []).map((u) => ({ value: u.id, label: u.name })),
    [orgUnitsQ.data],
  );
  const reportsToOptions = useMemo(
    () => positions.filter((p) => p.id !== positionId).map((p) => ({ value: p.id, label: p.title })),
    [positions, positionId],
  );
  // Üretim merkezleri (uretim_merkezi Location) — pozisyonu bir merkeze atamak için (opsiyonel).
  const centersQ = trpc.location.list.useQuery(undefined, { enabled: open, staleTime: 60_000 });
  const centerOptions = useMemo(
    () => ((centersQ.data ?? []) as Array<{ id: string; type: string; name: string; code: string }>)
      .filter((l) => l.type === "uretim_merkezi")
      .map((l) => ({ value: l.id, label: l.name, tail: l.code })),
    [centersQ.data],
  );

  const createMut = trpc.hr.position.create.useMutation();
  const updateMut = trpc.hr.position.update.useMutation();
  const jdMut = trpc.hr.jobDescription.upsert.useMutation();
  const busy = createMut.isPending || updateMut.isPending || jdMut.isPending;

  const clean = (arr: string[]) => arr.map((s) => s.trim()).filter(Boolean);
  const titleOk = title.trim().length > 0;

  async function submit() {
    if (!titleOk) { setErr("Pozisyon başlığı zorunlu."); return; }
    const hc = parseInt(headcount, 10);
    if (!Number.isFinite(hc) || hc < 1 || hc > 9999) { setErr("Kadro 1 ile 9999 arasında olmalı."); return; }
    setErr(null);

    const resp = clean(responsibilities);
    const reqs = clean(requirements);
    const sum = summary.trim();
    const hasJd = !!sum || resp.length > 0 || reqs.length > 0;

    try {
      let pid = positionId;
      if (isEdit && positionId) {
        await updateMut.mutateAsync({
          id: positionId,
          title: title.trim(),
          orgUnitId: orgUnitId, // null → bağı temizle
          reportsToId: reportsToId,
          headcount: hc,
          isManager,
          isShiftBased,
          isDeclarable,
          productionCenterId: productionCenterId,
          employmentType,
          isActive,
        });
      } else {
        const res = await createMut.mutateAsync({
          title: title.trim(),
          orgUnitId: orgUnitId ?? undefined,
          reportsToId: reportsToId ?? undefined,
          headcount: hc,
          isManager,
          isShiftBased,
          isDeclarable,
          productionCenterId: productionCenterId ?? undefined,
          employmentType,
        });
        pid = res.id;
      }
      // Görev tanımı: içerik varsa ya da düzenlemede zaten bir tanım varsa (temizlemeyi
      // kalıcılaştırmak için) upsert et; yeni + boş tanımda gereksiz satır açma.
      if (pid && (hasJd || (isEdit && jdExisted))) {
        await jdMut.mutateAsync({
          positionId: pid,
          summary: sum || null,
          responsibilities: resp,
          requirements: reqs,
        });
      }
      qc.hr.position.invalidate();
      qc.hr.jobDescription.invalidate();
      onSaved();
      pushToast({ tone: "success", message: isEdit ? "Pozisyon güncellendi." : "Pozisyon eklendi." });
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  const loadingEdit = isEdit && (getQ.isLoading || !getQ.data);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="lg"
      dismissible={false}
      title={isEdit ? (getQ.data ? `${getQ.data.code} — Pozisyonu Düzenle` : "Pozisyonu Düzenle") : "Yeni Pozisyon"}
      subtitle={isEdit ? "Künye ve görev tanımı" : "Kod otomatik üretilir (POZ-yıl-sıra)"}
      footer={
        <div style={{ display: "flex", gap: 8, width: "100%", alignItems: "center" }}>
          <button type="button" className="btn-md primary" onClick={submit} disabled={busy || !titleOk || loadingEdit}>
            {busy ? "Kaydediliyor…" : isEdit ? "Kaydet" : "Oluştur"}
          </button>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={busy}>Vazgeç</button>
        </div>
      }
    >
      {err ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{err}</div> : null}

      {getQ.isError ? (
        <div className="empty-state"><p className="ttl">Pozisyon yüklenemedi</p><p className="sub">{getQ.error.message}</p></div>
      ) : loadingEdit ? (
        <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
      ) : (
        <>
          <div className="form-field">
            <label className="lbl" htmlFor="pos-title">Başlık <span className="req">*</span></label>
            <input
              id="pos-title"
              className="form-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Örn. Üretim Vardiya Amiri"
              maxLength={160}
              autoFocus={!isEdit}
            />
          </div>

          <div className="form-row">
            <div className="form-field">
              <span className="lbl">Birim</span>
              <SearchSelect
                value={orgUnitId}
                options={orgUnitOptions}
                onChange={setOrgUnitId}
                placeholder="Birim seç…"
                searchPlaceholder="Birim ara…"
                emptyText={orgUnitsQ.isLoading ? "Yükleniyor…" : "Birim yok"}
                icon="building"
              />
              <span className="help">Pozisyonun bağlı olduğu organizasyon birimi.</span>
            </div>
            <div className="form-field">
              <span className="lbl">Üst Pozisyon</span>
              <SearchSelect
                value={reportsToId}
                options={reportsToOptions}
                onChange={setReportsToId}
                placeholder="Üst pozisyon seç…"
                searchPlaceholder="Pozisyon ara…"
                emptyText="Uygun pozisyon yok"
                icon="user"
              />
              <span className="help">Bu pozisyonun raporladığı üst pozisyon.</span>
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label className="lbl" htmlFor="pos-headcount">Kadro <span className="req">*</span></label>
              <input
                id="pos-headcount"
                className="form-input"
                type="number"
                min={1}
                max={9999}
                inputMode="numeric"
                value={headcount}
                onChange={(e) => setHeadcount(e.target.value)}
              />
              <span className="help">Bu pozisyon için onaylı personel sayısı.</span>
            </div>
            <div className="form-field">
              <label className="lbl" htmlFor="pos-emptype">Çalışma Türü</label>
              <select
                id="pos-emptype"
                className="form-select"
                value={employmentType}
                onChange={(e) => setEmploymentType(e.target.value as EmploymentType)}
              >
                {(Object.keys(EMPLOYMENT_LABELS) as EmploymentType[]).map((k) => (
                  <option key={k} value={k}>{EMPLOYMENT_LABELS[k]}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-field">
            <span className="lbl">Yönetici pozisyonu mu?</span>
            <ToggleSwitch checked={isManager} onChange={setIsManager} label={isManager ? "Evet — yönetici kadrosu" : "Hayır"} />
          </div>

          <div className="form-row">
            <div className="form-field">
              <span className="lbl">Vardiyalı</span>
              <ToggleSwitch checked={isShiftBased} onChange={setIsShiftBased} label={isShiftBased ? "Evet — vardiyalı çalışır" : "Hayır"} />
              <span className="help">Bu pozisyon vardiya usulü çalışır.</span>
            </div>
            <div className="form-field">
              <span className="lbl">Beyana tabi</span>
              <ToggleSwitch checked={isDeclarable} onChange={setIsDeclarable} label={isDeclarable ? "Evet — devam beyanına tabi" : "Hayır"} />
              <span className="help">Üretim'de her vardiya kaç kişi çalıştığı beyan edilir.</span>
            </div>
          </div>

          <div className="form-field">
            <span className="lbl">Üretim Merkezi</span>
            <SearchSelect
              value={productionCenterId}
              options={centerOptions}
              onChange={setProductionCenterId}
              placeholder="Merkez bağımsız (tüm merkezler)"
              searchPlaceholder="Üretim merkezi ara…"
              emptyText={centersQ.isLoading ? "Yükleniyor…" : "Üretim merkezi yok"}
              clearable
              icon="factory"
            />
            <span className="help">Bir merkeze atanırsa devam beyanı <b>yalnız o merkezin</b> üretim ekranından yapılır. Boş bırakılırsa merkez bağımsızdır.</span>
          </div>

          {isEdit ? (
            <div className="form-field">
              <span className="lbl">Durum</span>
              <ToggleSwitch checked={isActive} onChange={setIsActive} label={isActive ? "Aktif" : "Pasif"} />
              <span className="help">Pasif pozisyonlar yeni atamalar için önerilmez.</span>
            </div>
          ) : null}

          {/*
            ÜCRETLENDİRME — yalnız süper-admin ve yalnız DÜZENLEMEDE.
            · Yeni pozisyonda henüz `positionId` yok; para kaydı bir pozisyona bağlanmak
              zorunda olduğu için önce pozisyon kaydedilir, sonra ücret girilir.
            · Kart, formun kendi verisinden BESLENMEZ (position.get para döndürmez) —
              kendi süper-admin uçlarından okur. Bu yüzden "Kaydet" düğmesiyle de ilgisi
              yoktur: ücret girişleri anında ve ayrı ayrı kaydedilir.
          */}
          {isEdit && isSuper && positionId ? (
            <PositionPayCard positionId={positionId} pushToast={pushToast} />
          ) : null}

          <div style={{ borderTop: "1px solid var(--neutral-200)", margin: "2px 0 14px", paddingTop: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--neutral-800)", marginBottom: 2 }}>Görev Tanımı</div>
            <p className="help" style={{ margin: "0 0 12px" }}>Pozisyonun özeti, sorumlulukları ve nitelik gereksinimleri. İsteğe bağlı.</p>

            <div className="form-field">
              <label className="lbl" htmlFor="pos-summary">Özet</label>
              <textarea
                id="pos-summary"
                className="form-textarea"
                rows={3}
                value={summary}
                maxLength={4000}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="Pozisyonun amacı ve kapsamı…"
              />
            </div>

            <div className="form-field">
              <span className="lbl">Sorumluluklar</span>
              <StringListEditor
                items={responsibilities}
                onChange={setResponsibilities}
                addLabel="Sorumluluk ekle"
                placeholder="Örn. Vardiya üretim planını yürütür"
              />
            </div>

            <div className="form-field">
              <span className="lbl">Nitelik / Gereksinimler</span>
              <StringListEditor
                items={requirements}
                onChange={setRequirements}
                addLabel="Gereksinim ekle"
                placeholder="Örn. Gıda mühendisliği lisansı"
              />
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
}
